import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, ChevronRight, Package, Clock, Truck, HeartPulse, Flame, ThermometerSun } from 'lucide-react';
import { useSession } from '@/lib/SessionContext';
import { Link } from 'react-router-dom';
import AsaAssistant from '@/components/shared/AsaAssistant';
import CuteEmoji from '@/components/shared/CuteEmoji';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function MarketingDashboard() {
  const { clientPOs, isLoading: loadingPOs } = useClientPOs();
  const { member } = useSession();

  const { data: dbBahan = [], isLoading: loadingBahan } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list() });
  const { data: dbRacikan = [], isLoading: loadingRacikan } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list() });

  // Fetch production orders to sync status
  const { data: productionOrders = [], isLoading: loadingProdOrders } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });

  const { data: clientAccounts = [], isLoading: loadingClients } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });
  const { data: returns = [], isLoading: loadingReturns } = useQuery({ queryKey: ['returns'], queryFn: () => base44.entities.ReturnOrder.list() });
  const queryClient = useQueryClient();

  const updateMoodMutation = useMutation({
    mutationFn: ({ id, manual_mood }) => base44.entities.ClientAccount.update(id, { manual_mood }),
    onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
    }
  });

  // Map ProductionOrder.status to user-friendly display label
  const mapProdStatus = (rawStatus) => {
    const map = {
      'pending': 'MENUNGGU PRODUKSI',
      'sedang_po_item': 'SEDANG PO ITEM',
      'antrian_produksi': 'ANTRIAN PRODUKSI',
      'ready_produksi': 'READY PRODUKSI',
      'diracik': 'PROSES PRODUKSI - MIXING',
      'menunggu_qc_mixing': 'MENUNGGU VALIDASI QC - MIXING',
      'maserasi': 'PROSES PRODUKSI - MASERASI',
      'menunggu_filling': 'MENUNGGU FILLING',
      'filling': 'PROSES PRODUKSI - FILLING',
      'menunggu_qc_filling': 'MENUNGGU VALIDASI QC - FILLING',
      'menunggu_packing': 'MENUNGGU PENGEMASAN',
      'packing': 'PROSES PRODUKSI - PENGEMASAN',
      'waiting_filling': 'PENGEMASAN SELESAI (MENUNGGU FILLING)',
      'menunggu_qc_packing': 'MENUNGGU VALIDASI QC - PENGEMASAN',
      'siap_kirim': 'SIAP KIRIM',
      'dalam_pengiriman': 'SEDANG DALAM PENGIRIMAN',
      'shipping': 'SEDANG DALAM PENGIRIMAN',
      'done': 'PESANAN SELESAI',
      'in_progress': 'PROSES PRODUKSI',
      'ready': 'READY PRODUKSI',
    };
    return map[rawStatus] || (rawStatus || '').replace(/_/g, ' ').toUpperCase();
  };

  // Sync client PO status with real production status
  const baseSyncedClientPOs = useMemo(() => {
    return clientPOs.map(po => {
      const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual + legJual;
      const totalTagihan = totalJual + nominalPPN;
      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);

      const realPO = productionOrders.find(p => 
        p.order_number === po.id || 
        (p.notes && p.notes.includes(po.id)) ||
        (p.order_number && po.id && String(p.order_number).trim() === String(po.id).trim())
      );
      
      let dynamicStatusProduksi = po.statusProduksi || 'BELUM NAIK PRODUKSI';
      if (realPO) {
        dynamicStatusProduksi = mapProdStatus(realPO.status);
      } else if (!realPO && totalBayar >= (totalTagihan * 0.7) && !po.statusProduksi?.includes('PROSES')) {
        dynamicStatusProduksi = 'BELUM NAIK PO';
      }

      return {
        ...po,
        statusProduksi: dynamicStatusProduksi
      };
    });
  }, [clientPOs, productionOrders]);

  const isOwner = member?.role === 'owner' || member?.role === 'developer';
  const [selectedPic, setSelectedPic] = useState('ALL');
  const [showAllLate, setShowAllLate] = useState(false);

  const availablePics = useMemo(() => {
    const pics = new Set();
    baseSyncedClientPOs.forEach(po => {
      const p = po.pic || po.pic_marketing || po.pic_brand;
      if (p) pics.add(p);
    });
    return Array.from(pics).sort();
  }, [baseSyncedClientPOs]);

  const syncedClientPOs = useMemo(() => {
    let list = baseSyncedClientPOs;
    if (!isOwner && member?.full_name) {
      const memName = member.full_name.trim().toLowerCase();
      list = list.filter(po => {
        const poPic = (po.pic || po.pic_marketing || po.pic_brand || '').trim().toLowerCase();
        return poPic === memName || poPic.includes(memName) || memName.includes(poPic);
      });
    } else if (selectedPic !== 'ALL') {
      const sel = selectedPic.trim().toLowerCase();
      list = list.filter(po => {
        const poPic = (po.pic || po.pic_marketing || po.pic_brand || '').trim().toLowerCase();
        return poPic === sel || poPic.includes(sel);
      });
    }
    return list;
  }, [baseSyncedClientPOs, isOwner, member, selectedPic]);

  // Filter strictly active POs that are currently in production
  const activePOs = useMemo(() => {
    return syncedClientPOs.filter(po => {
      const realPO = (productionOrders || []).find(p => 
        p.id === po.id ||
        p.order_number === po.id || 
        p.order_number === po.order_number ||
        (p.notes && p.notes.includes(po.id)) ||
        (p.order_number && po.id && String(p.order_number).trim() === String(po.id).trim())
      );
      
      if (!realPO) return false;
      
      const stProd = (realPO.status || '').toLowerCase();
      if (stProd === 'done' || realPO.is_shipped) return false;
      if (po.timeline?.some(t => t.status === 'Pesanan Selesai' && t.done)) return false;
      
      return true;
    });
  }, [syncedClientPOs, productionOrders]);

  // Calculate brand health stats (STRICTLY for verified ClientAccounts)
  const brandHealthStats = useMemo(() => {
    const brands = {};
    
    // Base STRICTLY from verified ClientAccounts
    let filteredAccounts = clientAccounts.filter(ca => ca.status === 'verified');
    if (!isOwner && member?.full_name) {
      const memName = member.full_name.trim().toLowerCase();
      filteredAccounts = filteredAccounts.filter(ca => {
        const pic = (ca.pic_name || ca.pic || '').trim().toLowerCase();
        return pic === memName || pic.includes(memName) || memName.includes(pic);
      });
    } else if (selectedPic !== 'ALL') {
      const sel = selectedPic.trim().toLowerCase();
      filteredAccounts = filteredAccounts.filter(ca => {
        const pic = (ca.pic_name || ca.pic || '').trim().toLowerCase();
        return pic === sel || pic.includes(sel);
      });
    }

    filteredAccounts.forEach(ca => {
       if (ca.brand_name) {
          brands[ca.brand_name] = { 
            name: ca.brand_name, 
            activePOs: 0, 
            latePOs: 0, 
            nearLatePOs: 0, 
            returnsCount: 0, 
            health: 100, 
            latePOList: [], 
            lastPoDate: null, 
            clientAccountId: ca.id, 
            manualMood: ca.manual_mood || null 
          };
       }
    });

    // Update lastPoDate ONLY for existing verified brands
    syncedClientPOs.forEach(po => {
       const brandName = po.customerName ? po.customerName.split('/')[0].trim() : 'Satala';
       if (brands[brandName]) {
          const poDate = po.created_date ? new Date(po.created_date) : null;
          if (poDate && (!brands[brandName].lastPoDate || poDate > brands[brandName].lastPoDate)) {
              brands[brandName].lastPoDate = poDate;
          }
       }
    });
    
    activePOs.forEach(po => {
       const brandName = po.customerName ? po.customerName.split('/')[0].trim() : 'Satala';
       if (!brands[brandName]) return; // Only track verified brands

       brands[brandName].activePOs++;
       
       const stProd = (po.statusProduksi || '').toUpperCase();
       // Hanya PO yang BENAR-BENAR sedang berjalan di divisi produksi
       const isActuallyInProduction = ['PROSES PRODUKSI', 'DIRACIK', 'MASERASI', 'FILLING', 'PROSES PENGEMASAN', 'PACKING', 'QC', 'SIAP KIRIM'].some(s => stProd.includes(s));
       
       // Hanya hitung telat jika PO benar-benar sedang diproduksi dan deadline lewat
       if (po.deadline && isActuallyInProduction) {
          const dl = new Date(po.deadline);
          const now = new Date();
          const diffDays = Math.ceil((dl - now) / (1000 * 60 * 60 * 24));
          
          if (diffDays < 0) {
             brands[brandName].latePOs++;
             brands[brandName].health -= 30;
             brands[brandName].latePOList.push({ id: po.id, daysLate: Math.abs(diffDays) });
          } else if (diffDays <= 3) {
             brands[brandName].nearLatePOs++;
             brands[brandName].health -= 10;
          }
       }
    });
    
    returns.forEach(ret => {
       if (ret.return_status !== 'selesai' && brands[ret.brand_name]) {
          brands[ret.brand_name].returnsCount++;
          brands[ret.brand_name].health -= 20;
       }
    });
    
    const now = new Date();
    let result = Object.values(brands).map(b => {
       b.health = Math.max(0, Math.min(100, b.health));
       
       let daysSinceLastPO = 0;
       if (b.lastPoDate) {
          daysSinceLastPO = Math.floor((now - b.lastPoDate) / (1000 * 60 * 60 * 24));
       }
       b.daysSinceLastPO = daysSinceLastPO;
       
       if (b.manualMood) {
          b.mood = b.manualMood;
       } else if (b.activePOs === 0 && daysSinceLastPO > 210) {
          // 7+ bulan ga PO = Inactive (mati total)
          b.mood = 'inactive';
          b.health = 0;
       } else if (b.activePOs === 0 && daysSinceLastPO > 60) {
          // 2+ bulan ga PO = Pasif (merenung)
          b.mood = 'pasif';
          b.health = 0;
       } else if (b.health >= 80) {
          b.mood = 'happy';
       } else if (b.health >= 50) {
          b.mood = 'neutral';
       } else {
          b.mood = 'angry';
       }
       return b;
    });

    // Only show brands that are active, pasif, or inactive
    result = result.filter(b => b.activePOs > 0 || b.mood === 'pasif' || b.mood === 'inactive');
    result.sort((a, b) => b.activePOs === a.activePOs ? b.health - a.health : b.activePOs - a.activePOs);
    
    return result;
  }, [activePOs, syncedClientPOs, returns, clientAccounts, isOwner, member, selectedPic]);

  const activeBrands = brandHealthStats.filter(b => b.mood !== 'inactive' && b.mood !== 'pasif');
  const overallHealth = activeBrands.length > 0 
    ? Math.round(activeBrands.reduce((s, b) => s + b.health, 0) / activeBrands.length) 
    : 100;

  // Generate Asa's curated, smart insights
  const asaInsights = useMemo(() => {
    const insights = [];
    
    // 1. Unpaid Active POs (Aggregated)
    const unpaidPOs = activePOs.filter(po => {
      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      return totalBayar === 0;
    });
    if (unpaidPOs.length > 0) {
      const topSamples = unpaidPOs.slice(0, 2).map(p => `#${p.id} (${p.customerName || p.brand_name || 'Klien'})`).join(', ');
      insights.push({
        id: 'unpaid-pos',
        type: 'warning',
        text: `💳 Ada **${unpaidPOs.length} PO aktif** yang belum ada pembayaran DP masuk (${topSamples}${unpaidPOs.length > 2 ? ', dll' : ''}). Segera di-*follow up* ke klien agar jadwal produksi dapat segera dikunci!`
      });
    }

    // 2. Delivered but Unpaid Balance (Aggregated)
    const unpaidDelivered = activePOs.filter(po => {
      const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalTagihan = prodJual + legJual + nominalPPN;
      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const isLunas = totalBayar >= totalTagihan && totalTagihan > 0;
      return po.statusProduksi === 'SUDAH SAMPAI' && !isLunas;
    });
    if (unpaidDelivered.length > 0) {
      insights.push({
        id: 'delivered-unpaid',
        type: 'alert',
        text: `🚚 Ada **${unpaidDelivered.length} pesanan yang barangnya sudah sampai** ke klien namun belum lunas. Segera lakukan penagihan sisa pelunasan ya!`
      });
    }

    // 3. Paid but Pending Production (Aggregated)
    const pendingProdPOs = activePOs.filter(po => {
      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const statusProduksi = (po.statusProduksi || '').toUpperCase();
      return totalBayar > 0 && ['BELUM NAIK PO', 'MENUNGGU PRODUKSI', 'BELUM NAIK PRODUKSI'].includes(statusProduksi);
    });
    if (pendingProdPOs.length > 0) {
      insights.push({
        id: 'paid-pending-prod',
        type: 'info',
        text: `⚙️ Ada **${pendingProdPOs.length} PO** yang pembayaran DP/lunasnya sudah masuk tetapi masih berstatus *Menunggu Produksi*. Pastikan SPK sudah di-*submit* ke tim produksi.`
      });
    }

    // 4. BPOM & HAKI Checks (Aggregated)
    const unbpomBrands = new Set();
    const unhakiBrands = new Set();
    activePOs.forEach(po => {
      const brandName = po.customerName ? po.customerName.split('/')[0].trim() : 'Satala';
      (po.products || []).forEach(p => {
        const variantName = p.nama_product || p.variant_name;
        const racikan = dbRacikan.find(r => r.nama_brand === brandName && r.nama_varian === variantName);
        const bahan = dbBahan.find(b => b.nama_brand === brandName && b.nama_varian === variantName);
        if (racikan && !racikan.is_bpom) unbpomBrands.add(brandName);
        if ((bahan && !bahan.is_haki) || (racikan && !racikan.is_haki)) unhakiBrands.add(brandName);
      });
    });
    if (unbpomBrands.size > 0) {
      insights.push({
        id: 'bpom-check',
        type: 'alert',
        text: `📋 Terdeteksi ada varian pada **${unbpomBrands.size} brand aktif** (${Array.from(unbpomBrands).slice(0, 3).join(', ')}) yang belum terdaftar BPOM di sistem. Tolong dipastikan kelengkapan legalitasnya!`
      });
    }
    if (unhakiBrands.size > 0) {
      insights.push({
        id: 'haki-check',
        type: 'warning',
        text: `🛡️ Ada **${unhakiBrands.size} brand aktif** (${Array.from(unhakiBrands).slice(0, 3).join(', ')}) yang belum terdaftar HAKI. Pastikan perlindungan merek klien tetap terjaga.`
      });
    }

    // 5. Client Mood & Overall Health (Summary)
    if (brandHealthStats.length > 0) {
      const angryBrands = brandHealthStats.filter(b => b.mood === 'angry');
      if (angryBrands.length > 0) {
        insights.push({
          id: 'angry-brands',
          type: 'alert',
          text: `🚨 Perhatian: Ada **${angryBrands.length} brand klien** (${angryBrands.slice(0, 2).map(b => b.name).join(', ')}) yang skor kepuasannya menurun. Segera cek kendala pesanannya agar hubungan tetap terjaga!`
        });
      } else if (overallHealth >= 80) {
        insights.push({
          id: 'health-great',
          type: 'info',
          text: `Wah mantap! Skor kesehatan klien kita rata-rata di angka **${overallHealth}%** 🚀. Sebagian besar klien dalam kondisi *happy*. Pertahankan performanya!`
        });
      }
    }

    if (insights.length === 0) {
      insights.push({
        id: 'all-good',
        type: 'info',
        text: `Semua status pesanan dan kepuasan klien dalam kondisi baik! Tidak ada kendala mendesak saat ini. ✨`
      });
    }
    
    return insights;
  }, [activePOs, dbBahan, dbRacikan, brandHealthStats, overallHealth]);

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-[#E6D5B8]/40 pb-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl md:text-3xl font-black text-slate-800 tracking-tight">Dashboard Marketing</h1>
          <p className="text-[9px] md:text-xs text-slate-500">
            Selamat datang, {member?.full_name || 'Tim Marketing'}. Pantau progres PO Anda dan dengarkan asisten pintar kita.
          </p>
        </div>
        
        {isOwner && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">Filter PIC:</span>
            <Select value={selectedPic} onValueChange={setSelectedPic}>
              <SelectTrigger className="w-[200px] h-10 bg-white border-slate-200">
                <SelectValue placeholder="Semua PIC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua PIC</SelectItem>
                {availablePics.map(pic => (
                  <SelectItem key={pic} value={pic}>{pic}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Asa AI Panel */}
      {loadingPOs || loadingBahan || loadingRacikan || loadingProdOrders ? (
        <Card className="border shadow-sm bg-white rounded-3xl overflow-hidden p-6 animate-pulse">
           <div className="h-6 w-1/3 bg-slate-200 rounded mb-4"></div>
           <div className="h-4 w-1/2 bg-slate-200 rounded mb-2"></div>
           <div className="h-4 w-2/3 bg-slate-200 rounded"></div>
        </Card>
      ) : (
        <AsaAssistant 
          insights={asaInsights} 
          memberName={member?.full_name?.split(' ')[0]} 
          theme="default" 
        />
      )}

      {/* Client Health & Mood Section */}
      <div>
        <h3 className="text-lg md:text-xl font-black text-slate-800 tracking-tight mb-4 flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-rose-500" /> Kesehatan & Suasana Klien
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Termometer Kinerja (Vertical Slider) */}
          <Card className="lg:col-span-1 border-white/60 bg-white/80  shadow-sm overflow-hidden flex flex-col rounded-[2rem]">
            <CardContent className="p-6 flex flex-col h-full">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 text-center">Overall Health</p>
              
              <div className="flex-1 flex items-center justify-center py-6 min-h-[220px]">
                 <div className="relative flex justify-center items-stretch h-full w-24">
                   
                   {/* Stacked Emojis */}
                   <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-1 z-10 h-full">
                     <div title="Happy (100-80%)" className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center p-0.5"><CuteEmoji mood="happy" className="w-full h-full" /></div>
                     <div title="Neutral (79-50%)" className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center p-0.5"><CuteEmoji mood="neutral" className="w-full h-full" /></div>
                     <div title="Angry (<50%)" className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center p-0.5"><CuteEmoji mood="angry" className="w-full h-full" /></div>
                   </div>

                   {/* Vertical Track Line */}
                   <div className="absolute left-[2.75rem] top-2 bottom-2 w-2.5 bg-slate-100 rounded-full shadow-inner border border-slate-200/50" />
                   
                   {/* Vertical Fill Gradient */}
                   <div className="absolute left-[2.75rem] bottom-2 w-2.5 bg-gradient-to-t from-rose-500 via-amber-400 to-emerald-500 rounded-full shadow-sm transition-all duration-1000 origin-bottom" 
                        style={{ height: `max(2%, calc(${overallHealth}% - 1rem))` }} />

                   {/* Slider Pointer */}
                   <div className="absolute left-[3rem] flex items-center transition-all duration-1000 z-20" 
                        style={{ bottom: `calc(${overallHealth}% - 0.75rem)`, transform: 'translateY(50%)' }}>
                      <div className="w-0 h-0 border-t-[8px] border-t-transparent border-r-[10px] border-r-slate-800 border-b-[8px] border-b-transparent -ml-1"></div>
                      <div className="bg-slate-800 text-white text-[11px] font-black px-2.5 py-1 rounded-md shadow-lg ml-1 whitespace-nowrap">
                         {overallHealth}%
                      </div>
                   </div>
                 </div>
              </div>
              
              <div className="text-center mt-auto pt-4 border-t border-slate-100">
                 <h4 className="text-xs md:text-sm font-black text-slate-800">{overallHealth >= 80 ? 'Sangat Sehat' : overallHealth >= 50 ? 'Perlu Perhatian' : 'Kritis'}</h4>
                 <p className="text-[9px] md:text-[10px] text-slate-500 mt-1 leading-relaxed">Persentase kesehatan dari {activeBrands.length} brand aktif berdasarkan ketepatan waktu & komplain.</p>
              </div>
            </CardContent>
          </Card>

          {/* Client Mood Cards */}
          <div className="lg:col-span-3">
             {brandHealthStats.length === 0 ? (
               <div className="h-full flex flex-col items-center justify-center text-slate-400 border border-dashed border-slate-200 rounded-3xl p-8 bg-slate-50/50">
                  <ThermometerSun className="w-10 h-10 mb-2 opacity-20" />
                  <p className="text-sm font-medium">Belum ada data klien.</p>
               </div>
             ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                 {brandHealthStats.map(b => {
                   const isPasifOrInactive = b.mood === 'pasif' || b.mood === 'inactive';
                   const statusColor = b.mood === 'happy' ? 'bg-emerald-50 border-emerald-200' 
                     : b.mood === 'neutral' ? 'bg-amber-50 border-amber-200' 
                     : b.mood === 'angry' ? 'bg-rose-50 border-rose-200'
                     : b.mood === 'pasif' ? 'bg-slate-100 border-slate-200 opacity-75'
                     : 'bg-slate-100 border-slate-200 grayscale opacity-60';
                   
                   return (
                     <Card key={b.name} className={`relative overflow-hidden border ${statusColor} transition-all rounded-[2rem]`}>
                       <CardContent className="p-4 space-y-3">
                         <div className="flex justify-between items-start">
                           <div>
                             <h4 className="font-black text-slate-800 text-sm md:text-base">{b.name}</h4>
                             {b.mood === 'pasif' ? (
                                <p className="text-[9px] md:text-[10px] text-slate-500 font-bold tracking-wider mt-0.5">Pasif</p>
                             ) : b.mood === 'inactive' ? (
                                <p className="text-[9px] md:text-[10px] text-slate-400 font-bold tracking-wider mt-0.5">Tidak Aktif</p>
                             ) : (
                                <p className="text-[9px] md:text-[10px] text-slate-500 font-bold tracking-wider mt-0.5">Sistem: {b.health}% Health</p>
                             )}
                           </div>
                           <div className="w-10 h-10 filter drop-shadow-sm" title={b.mood === 'inactive' ? 'Klien Tidak Aktif (7+ bln)' : b.mood === 'pasif' ? 'Klien Pasif (2+ bln)' : `Health Score: ${b.health}%`}>
                             <CuteEmoji mood={b.mood} className="w-10 h-10" />
                           </div>
                         </div>
                         
                         <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
                            {b.mood === 'pasif' && <span className="flex items-center gap-1 text-slate-500 font-bold">2+ Bulan Ga PO 💤</span>}
                            {b.mood === 'inactive' && <span className="flex items-center gap-1 text-slate-400 font-bold">7+ Bulan Ga PO 💀</span>}
                            {!isPasifOrInactive && b.latePOs > 0 && <span className="flex items-center gap-1 text-rose-600 font-bold"><Flame className="w-3 h-3" /> {b.latePOs} PO Telat</span>}
                            {!isPasifOrInactive && b.nearLatePOs > 0 && <span className="flex items-center gap-1 text-amber-600 font-bold"><Clock className="w-3 h-3" /> {b.nearLatePOs} Hampir Telat</span>}
                            {!isPasifOrInactive && b.returnsCount > 0 && <span className="flex items-center gap-1 text-rose-600 font-bold"><Package className="w-3 h-3" /> {b.returnsCount} Return</span>}
                            {!isPasifOrInactive && b.latePOs === 0 && b.nearLatePOs === 0 && b.returnsCount === 0 && <span className="text-emerald-600 font-bold">Semua Aman Lancar 🚀</span>}
                         </div>

                         {/* Manual Override Tools */}
                         <div className="pt-3 mt-1 border-t border-slate-200/50 flex gap-1 justify-between items-center">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Set Mood:</span>
                            <div className="flex bg-white rounded-lg shadow-xs border border-slate-100 overflow-hidden items-center">
                              <button onClick={() => updateMoodMutation.mutate({ id: b.clientAccountId, manual_mood: 'happy' })} disabled={!b.clientAccountId} className={`p-1 hover:bg-slate-50 transition-colors ${b.manualMood === 'happy' ? 'bg-emerald-100 grayscale-0 opacity-100' : ''}`}><CuteEmoji mood="happy" className="w-6 h-6" /></button>
                              <button onClick={() => updateMoodMutation.mutate({ id: b.clientAccountId, manual_mood: 'neutral' })} disabled={!b.clientAccountId} className={`p-1 border-l border-r border-slate-100 hover:bg-slate-50 transition-colors ${b.manualMood === 'neutral' ? 'bg-amber-100 grayscale-0 opacity-100' : ''}`}><CuteEmoji mood="neutral" className="w-6 h-6" /></button>
                              <button onClick={() => updateMoodMutation.mutate({ id: b.clientAccountId, manual_mood: 'angry' })} disabled={!b.clientAccountId} className={`p-1 hover:bg-slate-50 transition-colors ${b.manualMood === 'angry' ? 'bg-rose-100 grayscale-0 opacity-100' : ''}`}><CuteEmoji mood="angry" className="w-6 h-6" /></button>
                              <button onClick={() => updateMoodMutation.mutate({ id: b.clientAccountId, manual_mood: null })} disabled={!b.clientAccountId || !b.manualMood} className={`px-2 py-1.5 border-l border-slate-100 text-[9px] font-black hover:bg-slate-50 transition-colors ${!b.manualMood ? 'text-slate-300' : 'text-slate-600'}`}>AUTO</button>
                            </div>
                         </div>
                       </CardContent>
                     </Card>
                   );
                 })}
               </div>
             )}
          </div>
        </div>

        {/* Warning Board for Late POs */}
        {(() => {
          const allLatePOs = brandHealthStats.flatMap(b => b.latePOList.map(l => ({ ...l, brand: b.name })));
          if (allLatePOs.length === 0) return null;
          const displayPOs = showAllLate ? allLatePOs : allLatePOs.slice(0, 8);
          return (
            <div className="mt-4 p-4 rounded-2xl bg-rose-50 border border-rose-200">
               <div className="flex items-center justify-between text-rose-700 font-black mb-3">
                 <div className="flex items-center gap-2">
                   <AlertTriangle className="w-5 h-5" /> Peringatan: Deadline Terlewat ({allLatePOs.length} PO)
                 </div>
                 {allLatePOs.length > 8 && (
                   <button
                     onClick={() => setShowAllLate(!showAllLate)}
                     className="text-xs font-bold text-rose-700 hover:text-rose-900 underline cursor-pointer"
                   >
                     {showAllLate ? 'Tampilkan Lebih Sedikit' : `+${allLatePOs.length - 8} PO Lainnya`}
                   </button>
                 )}
               </div>
               <div className="flex flex-wrap gap-2">
                 {displayPOs.map((lateItem, idx) => (
                    <Link key={idx} to={`/office/marketing/invoices`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-[11px] font-bold hover:shadow-sm transition-all text-slate-700">
                      <span className="text-rose-600 font-mono">#{lateItem.id}</span>
                      <span>{lateItem.brand}</span>
                      <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded text-[9px]">TELAT {lateItem.daysLate} HARI</span>
                    </Link>
                 ))}
               </div>
            </div>
          );
        })()}
      </div>

      {/* Progress PO */}
      <div>
        <h3 className="text-lg md:text-xl font-black text-slate-800 tracking-tight mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-[#8C6208]" /> Progres PO Aktif
        </h3>
        {activePOs.length === 0 ? (
           <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
             <Package className="w-16 h-16 mb-4 opacity-20" />
             <p className="text-lg font-medium text-slate-500">Belum ada PO yang aktif</p>
           </div>
        ) : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
             {activePOs.map(po => {
               const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
               const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
               const totalJual = prodJual + legJual;
               const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
               
               const isShipping = ['siap_kirim', 'dalam_pengiriman', 'pengiriman'].includes(po.statusProduksi?.toLowerCase().replace(/ /g, '_'));
               
               const prodCount = (po.products || []).length;
               const prodSubtitle = prodCount > 1
                 ? `Multi (${prodCount} Produk: ${po.products.map(p => p.nama_product || p.variant_name).filter(Boolean).slice(0, 2).join(', ')}${prodCount > 2 ? '...' : ''})`
                 : (po.products?.[0]?.nama_product || po.products?.[0]?.variant_name || 'Produk');

               return (
                  <Link to={`/office/marketing/invoices?search=${encodeURIComponent(po.id)}`} key={po.id} title={`Buka Invoice #${po.id}`}>
                    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-white/60 overflow-hidden bg-white/80  rounded-[2rem]">
                      <CardContent className="p-6">
                       {/* Header */}
                       <div className="flex justify-between items-start mb-4">
                          <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                               <span className="text-xs text-slate-400 font-mono font-bold">#{po.id}</span>
                               <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${
                                 po.statusBayar === 'LUNAS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                 po.statusBayar?.startsWith('DP') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                 'bg-rose-50 text-rose-700 border-rose-200'
                               }`}>
                                 {po.statusBayar || 'BELUM BAYAR'}
                               </span>
                            </div>
                            <h3 className="font-display text-base md:text-lg font-black tracking-tight text-[#302621] truncate">{po.customerName?.split('/')[0] || po.brand_name || 'Brand'}</h3>
                            <p className="text-[10px] md:text-xs font-medium text-slate-500 truncate mt-0.5">{prodSubtitle}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-200 group-hover:text-[#8C6208] transition-colors mt-2 shrink-0" />
                       </div>

                       {/* Visual Progres */}
                       <div className="py-3 px-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                         <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm shrink-0 ${
                           isShipping ? 'bg-blue-100 text-blue-600 border border-blue-200' :
                           po.statusProduksi === 'SELESAI PRODUKSI' ? 'bg-emerald-100 text-emerald-600 border border-emerald-200' :
                           'bg-amber-100 text-amber-600 border border-amber-200'
                         }`}>
                           {isShipping ? <Truck className="w-5 h-5" /> :
                            po.statusProduksi === 'SELESAI PRODUKSI' ? <CheckCircle2 className="w-5 h-5" /> :
                            <Clock className="w-5 h-5" />}
                         </div>
                         <div className="min-w-0">
                           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Status Produksi</p>
                           <p className="text-sm font-bold text-slate-700 truncate">{po.statusProduksi || 'BELUM NAIK PRODUKSI'}</p>
                         </div>
                       </div>

                       {/* Footer */}
                       <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                         <div>
                           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Tagihan</p>
                           <p className="text-xs font-bold text-slate-700 mt-0.5">Rp {fmt(totalJual)}</p>
                         </div>
                         <div className="text-right">
                           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Telah Dibayar</p>
                           <p className="text-xs font-bold text-[#8C6208] mt-0.5">Rp {fmt(totalBayar)}</p>
                         </div>
                       </div>
                     </CardContent>
                   </Card>
                 </Link>
               );
             })}
           </div>
        )}
      </div>
    </div>
  );
}
